/**
 * A08 — Flux de Trésorerie: FE form ↔ BE situation_flux_tresorerie column mapping.
 * Writable columns only (excludes id, created_at, updated_at, and server-computed totals).
 */

import { getBrandDefaultCurrency } from '../../../shared/utils/brandCurrency';

export const FLUX_WRITABLE_FIELDS = [
  'flux_ref',
  'exercice_fiscal',
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'type_periode',
  'methode_calcul',
  'statut_flux',
  'devise',
  'taux_conversion',
  'tresorerie_debut_periode',
  'flux_exploitation_encaissements',
  'flux_exploitation_decaissements',
  'flux_investissement_acquisitions',
  'flux_investissement_cessions',
  'flux_financement_emprunts',
  'flux_financement_remboursements',
  'flux_financement_dividendes',
  'capacite_autofinancement',
  'variation_bfr',
  'flux_tresorerie_libre',
  'flux_tresorerie_disponible',
  'detail_encaissements_clients',
  'detail_decaissements_fournisseurs',
  'detail_salaires_charges',
  'detail_impots_taxes',
  'detail_acquisitions',
  'detail_cessions',
  'detail_emprunts',
  'detail_remboursements',
  'ratios_tresorerie',
  'couverture_charges_fixes',
  'delai_ecoulement_stocks',
  'delai_recouvrement_clients',
  'delai_paiement_fournisseurs',
  'cycle_exploitation_jours',
  'besoin_tresorerie_exploitation',
  'previsions_tresorerie',
  'scenarios_stress_test',
  'risques_liquidite',
  'mesures_amelioration',
  'commentaires_exploitation',
  'commentaires_investissement',
  'commentaires_financement',
  'notes_explicatives',
  'annexes',
  'validation_tresorier',
  'validation_daf',
  'conformite_pcn',
  'conformite_ifrs',
  'notes',
  'metadata',
  'created_by',
];

/** Server-computed; included when mapping API → form for display, omitted on write. */
export const FLUX_COMPUTED_FIELDS = [
  'flux_exploitation_net',
  'flux_investissement_net',
  'flux_financement_net',
  'flux_tresorerie_total',
  'tresorerie_fin_periode',
  'variation_tresorerie',
];

const DATE_FIELDS = new Set([
  'periode_debut',
  'periode_fin',
  'date_etablissement',
]);

const DECIMAL_FIELDS = new Set([
  'taux_conversion',
  'tresorerie_debut_periode',
  'flux_exploitation_encaissements',
  'flux_exploitation_decaissements',
  'flux_investissement_acquisitions',
  'flux_investissement_cessions',
  'flux_financement_emprunts',
  'flux_financement_remboursements',
  'flux_financement_dividendes',
  'capacite_autofinancement',
  'variation_bfr',
  'flux_tresorerie_libre',
  'flux_tresorerie_disponible',
  'couverture_charges_fixes',
  'besoin_tresorerie_exploitation',
]);

const INTEGER_FIELDS = new Set([
  'delai_ecoulement_stocks',
  'delai_recouvrement_clients',
  'delai_paiement_fournisseurs',
  'cycle_exploitation_jours',
]);

const JSON_FIELDS = new Set([
  'detail_encaissements_clients',
  'detail_decaissements_fournisseurs',
  'detail_salaires_charges',
  'detail_impots_taxes',
  'detail_acquisitions',
  'detail_cessions',
  'detail_emprunts',
  'detail_remboursements',
  'ratios_tresorerie',
  'previsions_tresorerie',
  'scenarios_stress_test',
  'risques_liquidite',
  'mesures_amelioration',
  'notes_explicatives',
  'annexes',
  'metadata',
]);

export const EMPTY_FLUX_FORM = Object.freeze({
  ...FLUX_WRITABLE_FIELDS.reduce((acc, field) => {
    acc[field] = JSON_FIELDS.has(field) ? {} : '';
    return acc;
  }, {}),
  ...FLUX_COMPUTED_FIELDS.reduce((acc, field) => {
    acc[field] = '';
    return acc;
  }, {}),
  devise: getBrandDefaultCurrency(),
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const parseJsonField = (value) => {
  if (value == null || value === '') return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const toFormScalar = (field, value) => {
  if (value == null) {
    return JSON_FIELDS.has(field) ? {} : '';
  }
  if (JSON_FIELDS.has(field)) return parseJsonField(value);
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  return value;
};

/** Map API row → form state (writable + computed columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_FLUX_FORM };
  [...FLUX_WRITABLE_FIELDS, ...FLUX_COMPUTED_FIELDS].forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

/** Map form state → API payload (typed for situation_flux_tresorerie). */
export const buildFluxPayload = (formData = {}) => {
  const payload = {};

  FLUX_WRITABLE_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (DECIMAL_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseFloat(raw) || 0;
      return;
    }

    if (INTEGER_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseInt(raw, 10) || 0;
      return;
    }

    if (DATE_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    if (JSON_FIELDS.has(field)) {
      payload[field] = parseJsonField(raw);
      return;
    }

    payload[field] = raw === '' || raw == null ? null : raw;
  });

  return payload;
};
